/**
 * @fileoverview Shared device-aware preview around the canonical conductor shell.
 * @module inspection-renderer/preview
 *
 * Responsibilities:
 * - Own preview session state, device framing, form navigation, and Session Data.
 * - Render every form kind through InspectionConductorShell (content-only) and InspectionRenderer.
 * - Expose optional host module and response callbacks without owning persistence.
 *
 * Dependencies:
 * - InspectionConductorShell, DataSlideIn, and the in-memory PersistAdapter.
 * - @firesquirrel/core-domain rule evaluation and canonical response types.
 *
 * Architecture:
 * - Preview state is in-memory; the host supplies canonical forms and optional
 *   data-resolution, attachment, module, and response callback seams.
 * - Visible forms come from the shell callback; the active form is owned here and
 *   fed back to the shell as controlled state.
 *
 * Notes:
 * - Device chrome is presentation-only; form behavior remains package-owned.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, ClipboardList, Monitor, Smartphone, Tablet, X } from 'lucide-react';
import {
  evaluateAllFormRulesWithTrace,
  type CanonicalQuestionResponse,
  type FieldResponse,
  type InspectionForm,
  type InspectionRule,
  type ResolvedFormStateV2,
  type RuleTraceEntry,
} from '@firesquirrel/core-domain';
import { createSessionAdapter } from '../adapters/persistAdapter';
import { resolveFieldDefault } from '../fields/fieldDefaults';
import type { InspectionRendererProps } from '../InspectionRenderer';
import { DataSlideIn } from '../data-panel/DataSlideIn';
import { InspectionConductorShell } from '../shell/InspectionConductorShell';
import type { InspectionVisibleFormSummary } from '../shell/FormStepTabs';

type PreviewDevice = 'phone' | 'tablet' | 'desktop';

const DEVICE_WIDTHS: Record<PreviewDevice, number> = {
  phone: 390,
  tablet: 768,
  desktop: 1200,
};

const PANEL_MIN_WIDTH = 224;
const PANEL_MAX_WIDTH = 560;
const PANEL_WIDTH_RATIO = 0.35;

/**
 * @contract TemplatePreviewShellProps
 * Purpose: Configure the shared device-aware inspection template preview.
 * Fields: canonical forms, template metadata, rendering seams, and response callback.
 * Rules: Session state is in-memory and all form rendering uses the package shell.
 * Sync: Optional response notifications are emitted after session adapter changes.
 */
export interface TemplatePreviewShellProps {
  forms: InspectionForm[];
  templateName: string;
  templateId?: string;
  packageLabel?: string;
  templateRules?: readonly InspectionRule[];
  entityName?: string;
  entityAddress?: string;
  caseNumber?: string;
  caseId?: string;
  inspectorId?: string;
  attachmentPicker?: InspectionRendererProps['attachmentPicker'];
  resolveContext?: InspectionRendererProps['resolveContext'];
  renderModule?: InspectionRendererProps['renderModule'];
  saveStatus?: ReactNode;
  onClose?: () => void;
  onResponsesChange?: (responses: Record<string, CanonicalQuestionResponse>) => void;
}

/**
 * @component TemplatePreviewShell
 * Purpose: Render a shared inspection preview within phone, tablet, or desktop chrome.
 * Props: TemplatePreviewShellProps.
 * Behavior: Provides form navigation and a resizable Session Data panel around the shell.
 */
export function TemplatePreviewShell(props: TemplatePreviewShellProps): ReactNode {
  const {
    forms,
    templateName,
    templateId,
    packageLabel,
    templateRules,
    entityName,
    entityAddress,
    caseNumber,
    caseId = `preview-${templateId ?? 'template'}`,
    inspectorId = 'preview-inspector',
    attachmentPicker,
    resolveContext,
    renderModule,
    saveStatus,
    onClose,
    onResponsesChange,
  } = props;
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const [isDataPanelOpen, setIsDataPanelOpen] = useState(true);
  const [panelWidth, setPanelWidth] = useState(320);
  const [sessionRevision, setSessionRevision] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [activeFormId, setActiveFormId] = useState<string | undefined>();
  const [visibleForms, setVisibleForms] = useState<InspectionVisibleFormSummary[]>([]);
  const currentFormId = activeFormId ?? visibleForms[0]?.id;
  const currentFormIndex = Math.max(0, visibleForms.findIndex((form) => form.id === currentFormId));
  const goToFormIndex = (index: number) => {
    const target = visibleForms[index];
    if (target) setActiveFormId(target.id);
  };
  const [openedAt] = useState(() => new Date().toISOString());
  const previewContentRef = useRef<HTMLDivElement>(null);
  const [previewContentWidth, setPreviewContentWidth] = useState<number>();
  const maxPanelWidth = previewContentWidth === undefined
    ? PANEL_MAX_WIDTH
    : Math.min(PANEL_MAX_WIDTH, Math.floor(previewContentWidth * PANEL_WIDTH_RATIO));
  const minPanelWidth = Math.min(PANEL_MIN_WIDTH, maxPanelWidth);
  const constrainedPanelWidth = Math.max(minPanelWidth, Math.min(panelWidth, maxPanelWidth));

  useEffect(() => {
    const content = previewContentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      setPreviewContentWidth(entry.contentRect.width);
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const initialFormValues = useMemo(() => {
    const values: Record<string, Record<string, FieldResponse>> = {};
    for (const form of forms) {
      for (const field of form.fields) {
        const defaultValue = resolveFieldDefault(field);
        if (defaultValue == null || defaultValue === 'today' || defaultValue === 'now') continue;
        (values[form._id] ??= {})[field.id] = { value: defaultValue };
      }
    }
    return values;
  }, [forms]);
  const persistAdapter = useMemo(
    () => createSessionAdapter({ actorId: inspectorId, initialFormValues }),
    [initialFormValues, inspectorId],
  );

  useEffect(() => {
    const notify = () => {
      setSessionRevision((revision) => revision + 1);
      onResponsesChange?.(persistAdapter.getCanonicalResponses());
    };
    return persistAdapter.subscribe(notify);
  }, [onResponsesChange, persistAdapter]);

  const canonicalResponses = persistAdapter.getCanonicalResponses();
  const flatResponses = useMemo(() => {
    const responses: Record<string, FieldResponse> = {};
    for (const [fieldId, response] of Object.entries(canonicalResponses)) {
      responses[fieldId] = { value: response.value, isNA: response.isNA };
    }
    return responses;
  }, [canonicalResponses, sessionRevision]);
  const resolvedStates = useMemo<Record<string, ResolvedFormStateV2>>(() => {
    try {
      return evaluateAllFormRulesWithTrace(forms, flatResponses, templateRules ?? []);
    } catch {
      return {};
    }
  }, [flatResponses, forms, templateRules]);
  const rulesTrace = useMemo<RuleTraceEntry[]>(() => {
    const seen = new Set<string>();
    const trace: RuleTraceEntry[] = [];
    for (const state of Object.values(resolvedStates)) {
      for (const entry of state.trace ?? []) {
        if (seen.has(entry.ruleId)) continue;
        seen.add(entry.ruleId);
        trace.push(entry);
      }
    }
    return trace;
  }, [resolvedStates]);

  const handleResizeStart = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = constrainedPanelWidth;
    const handleMove = (moveEvent: globalThis.PointerEvent) => {
      setPanelWidth(Math.max(minPanelWidth, Math.min(maxPanelWidth, startWidth + startX - moveEvent.clientX)));
    };
    const handleUp = () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
    };
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  };

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const delta = event.key === 'ArrowLeft' ? 16 : -16;
    setPanelWidth((width) => Math.max(minPanelWidth, Math.min(maxPanelWidth, width + delta)));
  };

  const handleReset = () => {
    persistAdapter.reset();
    setResetKey((key) => key + 1);
    setSessionRevision((revision) => revision + 1);
  };

  const isFirstForm = currentFormIndex <= 0;
  const isLastForm = currentFormIndex >= visibleForms.length - 1;

  return (
    <section
      data-template-preview-shell
      data-preview-device={device}
      className="flex h-full min-h-0 w-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden bg-gray-100"
    >
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-3 py-2">
        <div role="group" aria-label="Preview device width" className="flex items-center gap-1">
          {([
            ['phone', Smartphone],
            ['tablet', Tablet],
            ['desktop', Monitor],
          ] as const).map(([width, Icon]) => (
            <button
              key={width}
              type="button"
              aria-label={width[0].toUpperCase() + width.slice(1)}
              aria-pressed={device === width}
              onClick={() => setDevice(width)}
              className={`rounded-md p-2 transition-colors ${
                device === width ? 'bg-orange-100 text-orange-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <Icon size={16} aria-hidden="true" />
            </button>
          ))}
        </div>
        <div role="group" aria-label="Form navigation" className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous form"
            onClick={() => goToFormIndex(currentFormIndex - 1)}
            disabled={isFirstForm}
            className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <span data-form-step-indicator className="min-w-[6.5rem] text-center text-xs font-medium text-gray-700">
            {visibleForms.length > 0 ? `Form ${currentFormIndex + 1} of ${visibleForms.length}` : 'No forms'}
          </span>
          <button
            type="button"
            aria-label="Next form"
            onClick={() => goToFormIndex(currentFormIndex + 1)}
            disabled={isLastForm}
            className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {saveStatus}
          <button
            type="button"
            aria-expanded={isDataPanelOpen}
            onClick={() => setIsDataPanelOpen((open) => !open)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <ClipboardList size={14} aria-hidden="true" />
            Session Data
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100"
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div ref={previewContentRef} data-preview-content className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col md:flex-row">
          <nav
            aria-label="Inspection forms"
            data-form-list
            className="flex shrink-0 gap-1 overflow-x-auto border-b border-gray-200 bg-white p-2 md:w-48 md:flex-col md:overflow-y-auto md:border-b-0 md:border-r"
          >
            {visibleForms.map((form, index) => {
              const selected = form.id === currentFormId;
              return (
                <button
                  key={form.id}
                  type="button"
                  data-form-list-item={form.id}
                  aria-current={selected ? 'step' : undefined}
                  onClick={() => setActiveFormId(form.id)}
                  className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-left text-xs md:shrink md:whitespace-normal ${
                    selected
                      ? 'bg-orange-50 font-semibold text-orange-800 md:border-r-2 md:border-orange-500'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                      selected ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="truncate">{form.name}</span>
                </button>
              );
            })}
          </nav>

          <div data-preview-scroll className="flex min-w-0 flex-1 justify-start overflow-auto bg-gray-100 p-4">
            <div
              data-device-frame
              className="mx-auto flex min-h-full shrink-0 flex-col overflow-hidden rounded-2xl bg-white shadow-md"
              style={device === 'desktop'
                ? { width: '100%', maxWidth: DEVICE_WIDTHS.desktop }
                : { width: DEVICE_WIDTHS[device] }}
            >
              {device === 'phone' && (
                <div aria-hidden="true" className="flex h-5 shrink-0 items-center justify-between bg-black px-3 text-[10px] text-white">
                  <span>9:41</span>
                  <span>•••</span>
                </div>
              )}
              <InspectionConductorShell
                presentationMode="template-preview"
                chrome="content-only"
                mode="preview"
                activeFormId={activeFormId}
                onActiveFormIdChange={setActiveFormId}
                onVisibleFormsChange={setVisibleForms}
                forms={forms}
                templateRules={templateRules}
                persistAdapter={persistAdapter}
                attachmentPicker={attachmentPicker}
                resolveContext={resolveContext}
                caseId={caseId}
                inspectorId={inspectorId}
                entityName={entityName}
                entityAddress={entityAddress}
                caseNumber={caseNumber}
                templateName={templateName}
                packageLabel={packageLabel}
                density={{ density: device === 'phone' ? 'mobile' : 'desktop' }}
                resetKey={resetKey}
                renderModule={renderModule}
                saveStatus={saveStatus}
                onClose={onClose}
              />
              {device === 'phone' && (
                <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 bg-gray-50 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => goToFormIndex(currentFormIndex - 1)}
                    disabled={isFirstForm}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => goToFormIndex(currentFormIndex + 1)}
                    disabled={isLastForm}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                  </button>
                </footer>
              )}
            </div>
          </div>
        </div>

        {isDataPanelOpen ? (
          <div
            data-session-data-panel
            className="flex min-w-0 shrink-0 border-l border-slate-200 bg-white"
            style={{
              width: constrainedPanelWidth,
              maxWidth: `${PANEL_WIDTH_RATIO * 100}%`,
              minWidth: `min(${PANEL_MIN_WIDTH}px, ${PANEL_WIDTH_RATIO * 100}%)`,
            }}
          >
            <div
              role="separator"
              aria-label="Resize session data panel"
              aria-orientation="vertical"
              aria-valuemin={minPanelWidth}
              aria-valuemax={maxPanelWidth}
              aria-valuenow={constrainedPanelWidth}
              tabIndex={0}
              onPointerDown={handleResizeStart}
              onKeyDown={handleResizeKeyDown}
              className="w-1 shrink-0 cursor-col-resize bg-transparent hover:bg-orange-200 focus:bg-orange-200"
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-3 py-2">
                <h2 className="text-sm font-semibold text-slate-800">Session Data</h2>
                <button
                  type="button"
                  aria-label="Close Session Data"
                  onClick={() => setIsDataPanelOpen(false)}
                  className="rounded p-1 text-slate-500 hover:bg-slate-100"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </header>
              <div className="min-h-0 min-w-0 flex-1 overflow-auto">
                <DataSlideIn
                  mode="preview"
                  caseId={caseId}
                  inspectorId={inspectorId}
                  entityName={entityName}
                  templateName={templateName}
                  currentEventIndex={0}
                  openedAt={openedAt}
                  forms={forms}
                  resolvedStates={resolvedStates}
                  rulesTrace={rulesTrace}
                  responses={canonicalResponses}
                  violations={persistAdapter.getViolations()}
                  contactChanges={persistAdapter.getContactChanges()}
                  onReset={handleReset}
                />
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            aria-label="Open Session Data"
            onClick={() => setIsDataPanelOpen(true)}
            className="w-8 shrink-0 border-l border-slate-200 bg-white text-slate-500 hover:text-orange-700"
          >
            <ClipboardList size={15} aria-hidden="true" />
          </button>
        )}
      </div>
    </section>
  );
}
